import { QueryClient, useQuery } from '@tanstack/react-query'
import { getSession, putPrefs, type Prefs, type Session } from './api.ts'
import { setFontScale, setLatinOn } from './prefs.ts'

export const makeQueryClient = () =>
  new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false, staleTime: 60_000 } } })

export const ME_KEY = ['session'] as const
export const useSession = () => useQuery({ queryKey: ME_KEY, queryFn: getSession })

/** Device copy of the account's text size and Latin setting (they follow the user to a new device). */
export function applyAccountPrefs(p: Pick<Prefs, 'fontScale' | 'latinOn'>) {
  setFontScale(p.fontScale)
  setLatinOn(p.latinOn)
}

/**
 * Change account prefs: applied on this device at once, then saved. The session cache gets the server's answer, so the
 * next session refetch never brings an old value back. Throws when the save fails (the device copy stays).
 */
export async function updatePrefs(qc: QueryClient, patch: Partial<Prefs>) {
  if (patch.fontScale !== undefined) setFontScale(patch.fontScale)
  if (patch.latinOn !== undefined) setLatinOn(patch.latinOn)
  const { prefs } = await putPrefs(patch)
  qc.setQueryData<Session>(ME_KEY, (s) => (s?.state === 'ok' ? { ...s, me: { ...s.me, prefs } } : s))
  return prefs
}
